import React, { Component, createRef } from "react"
import { navigate } from "gatsby"
import Layout from "../components/layout"
import {
  ajax,
  API_CALL_STATUSES,
  getQueryParam,
  getRedirectPathFromUrl,
  getTokenRedirectPathFromUrl,
} from "../utils"
import Progress from "../components/progress"
import FancyInput from "../components/FancyInput"
import RIOLogo from "../assets/img/rio_wordmark.svg"
import PasswordInput from "../components/passwordInputs/PasswordInput"
import { window } from "browser-monads"

const { authURL, azureADEnabled, ssoEnabled } = window

const getErrorMessage = (err, response) => {
  switch (err?.status) {
    case 422:
      return null
    case 403:
      return "Please verify your email."
    case 401:
      return "Invalid username or password."
    case 500:
      return "Server error. Please try again later."
    default:
      return response?.error || "Failed to login."
  }
}

class Login extends Component {
  state = {
    loginStatus: API_CALL_STATUSES.IDLE,
    email: "",
    password: "",
    errorMessage: "",
    showCaptcha: false,
    grecaptchaValue: "",
    loginChallenge: getQueryParam("login_challenge"),
  }

  grecaptchaRef = createRef()

  onLoginSubmit = e => {
    e.preventDefault()
    const { email, password, loginChallenge } = this.state

    this.setState({ loginStatus: API_CALL_STATUSES.PROGRESS })

    ajax({
      path: `user/login${
        loginChallenge ? `?login_challenge=${loginChallenge}` : ""
      }`,
      type: "POST",
      data: { email, password, login_challenge: loginChallenge },
      success: res => {
        navigate(
          `/authRedirect/${getTokenRedirectPathFromUrl(
            res.data.token,
            res.data.expiryAt
          )}`
        )
      },
      error: (err, response) => {
        if (err.status === 302) {
          /**
           * Login flow for OIDC. (e.g., when using 'Login using rapyuta.io' in amr_ui)
           *
           * This redirect sends the user to the consent page (/auth/src/pages/consent.js)
           */
          window.location.href = response.data.redirect_to
        } else {
          this.setState({
            loginStatus: API_CALL_STATUSES.ERROR,
            errorMessage: getErrorMessage(err, response),
            showCaptcha: false,
          })
        }
      },
    })
  }

  onSSOLogin = () => {
    const { loginChallenge } = this.state
    const params = new URLSearchParams()
    if (loginChallenge) {
      params.set("login_challenge", loginChallenge)
    }
    const qs = params.toString()
    window.location.href = `${authURL}user/external/sso/login${qs ? `?${qs}` : ""}`
  }

  updateValue = e => {
    const {
      target: {
        dataset: { id },
        value,
      },
    } = e
    e.preventDefault()
    this.setState({
      [id]: value,
    })
  }

  render() {
    const {
      loginStatus,
      email,
      password,
      errorMessage,
      loginChallenge,
    } = this.state

    /**
     * Remember Me is also not supported when using rapyuta.io as OIDC provider.
     */
    const shouldShowRememberMe = !Boolean(loginChallenge)

    return (
      <Layout dark stickyFooter bgimage>
        {loginStatus === API_CALL_STATUSES.PROGRESS && <Progress />}

        <div className="container mx-auto flex justify-center items-center h-full">
          <div className="w-full max-w-md">
            <div className="relative flex flex-col w-full p-6 shadow-lg rounded-lg bg-white">
              <div className="text-center mb-8">
                <img
                  className="mx-auto h-12 w-auto"
                  src={RIOLogo}
                  alt="Rapyuta Robotics"
                />
              </div>
              {loginStatus === API_CALL_STATUSES.ERROR && errorMessage && (
                <p className="text-red-500 text-center mb-4">{errorMessage}</p>
              )}
              <form onSubmit={this.onLoginSubmit}>
                <div className="mb-4">
                  <FancyInput
                    data-id="email"
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Email address"
                    value={email}
                    onChange={this.updateValue}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="mb-4">
                  <PasswordInput
                    data-id="password"
                    name="password"
                    id="password"
                    placeholder="Password"
                    value={password}
                    onChange={this.updateValue}
                    autoComplete="password"
                    required
                  />
                </div>

                {shouldShowRememberMe && (
                  <div className="mb-4 flex items-center">
                    <input
                      id="rememberMe"
                      type="checkbox"
                      className="form-checkbox text-red-600 w-4 h-4"
                    />
                    <label
                      htmlFor="rememberMe"
                      className="ml-2 mt-1 text-sm text-gray-700"
                    >
                      Remember me
                    </label>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    className="w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition duration-150"
                  >
                    Sign In
                  </button>
                </div>
              </form>

              {/* Links */}
              <div className="flex justify-between mt-4 text-sm">
                <a
                  href={`/forgotPassword${getRedirectPathFromUrl()}`}
                  className="text-red-600 hover:underline"
                >
                  Forgot password?
                </a>
                <a
                  href={`/register${getRedirectPathFromUrl()}`}
                  className="text-red-600 hover:underline"
                >
                  Create new account
                </a>
              </div>

                <div className="flex items-center my-4">
                    <hr className="flex-grow border-gray-300" />
                    <span className="mx-3 text-sm text-gray-500">OR</span>
                    <hr className="flex-grow border-gray-300" />
                </div>
                <button
                    type="button"
                    onClick={this.onSSOLogin}
                    className="w-full flex items-center justify-center border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition duration-150"
                >
                    <svg
						className="w-5 h-5 mr-2"
						viewBox="0 0 21 21"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
                    >
						<path d="M0 0h10v10H0z" fill="#F25022" />
						<path d="M11 0h10v10H11z" fill="#7FBA00" />
						<path d="M0 11h10v10H0z" fill="#00A4EF" />
						<path d="M11 11h10v10H11z" fill="#FFB900" />
                    </svg>
                    Login with Microsoft
                </button>
            </div>
          </div>
        </div>
      </Layout>
    )
  }
}

export default Login
